import { Classes, Dialog } from "@blueprintjs/core"
import styled from "@emotion/styled"
import { ControlStatus } from "../../../../api/reconciliation"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import { SCIENT_COLORS } from "../../../../styled/scientColors"
import { ShareCountState, Verdict } from "./reconciliation.utils"

export const ReconciliationContainer = styled(FlexContainer)({
  gridArea: "content",
  height: "100%",
  padding: "16px",
  gap: "12px",
  flexDirection: "column",
  overflow: "hidden",
})

export const UploadBarContainer = styled(FlexContainer)({
  alignItems: "center",
  gap: "12px",
  padding: "8px 12px",
  backgroundColor: SCIENT_COLORS.darkGray2,
  borderRadius: "4px",
  flexShrink: 0,
})

// The verdict accent is the page's one loud element (reconciliation.utils,
// Verdict): green when every control passed, red when a control breached or
// could not be evaluated, amber when something needs a look before any
// control can run, neutral when there is nothing to judge.
const VERDICT_ACCENT: Record<NonNullable<Verdict>, string> = {
  passed: SCIENT_COLORS.green5,
  breached: SCIENT_COLORS.red4,
  review: SCIENT_COLORS.orange4,
}

export const SummaryBarContainer = styled(FlexContainer)<{
  verdict?: Verdict
}>(({ verdict }) => ({
  alignItems: "center",
  gap: "16px",
  padding: "8px 12px",
  backgroundColor: SCIENT_COLORS.darkGray2,
  borderRadius: "4px",
  flexWrap: "wrap",
  flexShrink: 0,
  borderLeft: `3px solid ${verdict ? VERDICT_ACCENT[verdict] : "transparent"}`,
}))

export const CommandBarSpacer = styled.div({
  marginLeft: "auto",
})

export const SummaryItem = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
})

export const SummaryLabel = styled.span({
  fontSize: "10px",
  color: SCIENT_COLORS.gray3,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
})

export const SummaryValue = styled.span({
  fontSize: "14px",
  fontWeight: 600,
  color: SCIENT_COLORS.lightGray5,
})

// Δ value echoes the bar's amber accent when the NAV pair needs a look.
export const SummaryDeltaValue = styled(SummaryValue)<{ alert?: boolean }>(
  ({ alert }) => ({
    color: alert ? SCIENT_COLORS.orange4 : undefined,
  }),
)

export const SummaryDivider = styled.div({
  width: "1px",
  height: "32px",
  backgroundColor: SCIENT_COLORS.darkGray4,
})

// A colour token at some opacity, for a tint that follows the token.
const withAlpha = (hex: string, alpha: number): string => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// A row whose price-gap control did not pass (CONTEXT.md, "Breach"): the
// grid's loudest mark. AG Grid's getRowStyle / cellStyle take style
// objects, so these are the named values they return.
export const PRICE_GAP_BREACH_CELL_STYLE = {
  color: SCIENT_COLORS.red4,
  fontWeight: 600,
}

export const PRICE_GAP_BREACH_PRICE_CELL_STYLE = {
  ...PRICE_GAP_BREACH_CELL_STYLE,
  backgroundColor: withAlpha(SCIENT_COLORS.red4, 0.18),
}


export const GridContainer = styled.div({
  flex: 1,
  width: "100%",
  minHeight: 0,
  "& .ag-theme-balham-dark .ag-cell.edited-cell": {
    backgroundColor: "rgba(255, 200, 80, 0.18)",
  },
})

export const StyledFileInput = styled.div({
  minWidth: 280,
})

// --- Share-class strip ---------------------------------------------------
// A compact data table living inside the summary bar: one header row, one row
// per class, right-aligned tabular figures. The Δ column is the 5bp split
// residual, a method diagnostic; the NAV control column carries the verdict.

export const ShareClassTable = styled.div({
  display: "grid",
  gridTemplateColumns: "auto repeat(8, auto)",
  columnGap: "16px",
  rowGap: "3px",
  alignItems: "center",
})

export const ShareClassHeaderCell = styled.span({
  fontSize: "10px",
  color: SCIENT_COLORS.gray3,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  textAlign: "right",
})

export const ShareClassCell = styled.span({
  fontSize: "13px",
  fontWeight: 600,
  color: SCIENT_COLORS.lightGray5,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
})

// One colour per control state across the strip: green when passed, red
// when breached, unverified (not passed) or edited away from the file's
// figure, amber once a share-count breach was accepted.
const CONTROL_STATE_COLOR: Record<ControlStatus | ShareCountState, string> = {
  passed: SCIENT_COLORS.green5,
  breached: SCIENT_COLORS.red4,
  unverified: SCIENT_COLORS.red4,
  edited: SCIENT_COLORS.red4,
  accepted: SCIENT_COLORS.orange4,
}

// The NAV control's result for one class: the gap in basis points.
export const NavControlCell = styled.span<{ status: ControlStatus }>(({ status }) => ({
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  color: CONTROL_STATE_COLOR[status],
  "&::before": {
    content: '"●"',
    fontSize: "8px",
    verticalAlign: "1px",
    marginRight: "5px",
  },
}))

// The share-count control's cell for one class (#1751): the file count
// against the known count or the operator's edit, with the accept action
// and the acceptance's comment beside it.
export const ShareCountCell = styled.span<{ state: ShareCountState }>(({ state }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  color: CONTROL_STATE_COLOR[state],
}))

// The price-gap control's tally in the bar: the breached (and unverified)
// row count, red when any row did not pass, green when none did.
export const ControlCountValue = styled(SummaryValue)<{ breached: boolean }>(
  ({ breached }) => ({
    color: breached ? SCIENT_COLORS.red4 : SCIENT_COLORS.green5,
  }),
)

// One breach (or unverified class) spelled out under the strip: target,
// both figures, gap and threshold — what to investigate before scrolling.
export const ControlBreachList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  maxWidth: "520px",
})

export const ControlBreachMessage = styled.span({
  fontSize: "12px",
  color: SCIENT_COLORS.red4,
  lineHeight: 1.4,
})

// The submit confirmation stays inside the window whatever the breach
// count: a day with dozens of price-gap breaches lists them all, and the
// list scrolls while the question and the two buttons stay in view.
export const SubmitDialog = styled(Dialog)({
  maxHeight: "85vh",
  width: "760px",
  maxWidth: "calc(100vw - 32px)",
  [`& .${Classes.DIALOG_BODY}`]: {
    overflowY: "auto",
    minHeight: 0,
  },
})

// The sentence above the breach list in the submit confirmation.
export const SubmitConfirmationNote = styled.p({
  fontSize: "12px",
  color: SCIENT_COLORS.gray4,
  lineHeight: 1.4,
})

export const ShareClassCode = styled.span({
  fontSize: "11px",
  fontWeight: 700,
  color: SCIENT_COLORS.lightGray5,
  backgroundColor: SCIENT_COLORS.darkGray4,
  padding: "2px 6px",
  borderRadius: "3px",
  minWidth: "28px",
  textAlign: "center",
})

// Inline editor for shares outstanding (#1631): looks like the read-only
// cell until focused; the amber tint mirrors the grid's edited-cell marker.
export const ShareClassSharesInput = styled.input<{ edited?: boolean }>(
  ({ edited }) => ({
    width: "80px",
    fontSize: "13px",
    fontWeight: 600,
    color: SCIENT_COLORS.lightGray5,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    backgroundColor: edited ? "rgba(255, 200, 80, 0.18)" : "transparent",
    border: `1px solid ${SCIENT_COLORS.darkGray4}`,
    borderRadius: "3px",
    padding: "1px 4px",
    "&:focus": {
      outline: "none",
      borderColor: SCIENT_COLORS.blue5,
    },
  }),
)

export const ShareClassCommentInput = styled.input({
  width: "180px",
  fontSize: "12px",
  color: SCIENT_COLORS.lightGray5,
  backgroundColor: "transparent",
  border: `1px solid ${SCIENT_COLORS.darkGray4}`,
  borderRadius: "3px",
  padding: "1px 6px",
  "&::placeholder": {
    color: SCIENT_COLORS.gray3,
  },
  "&:focus": {
    outline: "none",
    borderColor: SCIENT_COLORS.blue5,
  },
})

// The acceptance's comment, inside the share-count cell: narrower than
// the shares edit's, beside the count it explains.
export const ShareCountCommentInput = styled(ShareClassCommentInput)({
  width: "140px",
})

export const ShareClassMessage = styled.span({
  fontSize: "12px",
  color: SCIENT_COLORS.gray4,
  maxWidth: "420px",
  lineHeight: 1.4,
})

// A value in the bar that acts on click: dotted underline as the
// affordance, cursor pointer.
export const ClickableValue = styled.span({
  cursor: "pointer",
  textDecoration: "underline dotted",
  textUnderlineOffset: "3px",
})

// The submit confirmation's breach tables: one per control, compact,
// tabular figures, red where the control did not pass.
export const BreachTables = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  "& table": { fontSize: "12px", fontVariantNumeric: "tabular-nums", width: "100%" },
  "& th": { color: SCIENT_COLORS.gray4, fontWeight: 500, whiteSpace: "nowrap" },
  "& td": { whiteSpace: "nowrap" },
  "& td.num, & th.num": { textAlign: "right" },
  "& td.bad": { color: SCIENT_COLORS.red4, fontWeight: 600 },
  "& td.dim, & small": { color: SCIENT_COLORS.gray4 },
})

export const BreachTableTitle = styled.div({
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: SCIENT_COLORS.gray4,
  marginBottom: "4px",
})
